import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Chip, Avatar, Tooltip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { Verified as VerifiedIcon } from '@mui/icons-material';
import FollowButton from 'components/common/buttons/FollowButton';
import { useUserContext } from 'shared/contexts/UserContext';
import MeetingScheduleDialog from "components/common/dialogs/MeetingScheduleDialog";
import InviteToConsortiumDialog from "components/common/dialogs/InviteToConsortiumDialog";
import SuggestEntityDialog from "components/common/dialogs/SuggestEntityDialog";

import ActionButtons from 'components/social/profile/ActionButtons';


const StyledCard = styled(Card)(({ theme, selected }) => ({
  // Fill the grid cell: every card in a row has the same width and height (no gaps)
  height: '100%',
  width: '100%',
  minWidth: 0,
  // as tall as a card with an affiliation and two lines of skills: a profile not filled in yet looks the same size
  minHeight: 210,
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  position: 'relative',
  border: selected ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.sidebar}`,
  backgroundColor:'#fff', 
  // '#eeeeeeff',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
    border: '1px solid #e0e0e0',
  },
  '& .MuiCardContent-root': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    // phones (two cards a row): a little less padding leaves the action buttons their size
    [theme.breakpoints.down('sm')]: { padding: '10px 8px' },
    '&:last-child': {
      paddingBottom: '10px'
    }
  }
}));

const UserCard = ({
  user,
  isSelected,
  onSelect,
  onNavigate,
  followState,
}) => {
  // Suggest entity dialog state
  const [suggestDialogOpen, setSuggestDialogOpen] = useState(false);

  // Invite to consortium dialog state
  const [inviteToConsortiumDialogOpen, setInviteToConsortiumDialogOpen] = useState(false);

  // Meeting dialog state
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const theme = useTheme();
  const { user: me } = useUserContext() || {};
  // the action buttons are for connections (and staff); until then Connect on the left and, on the right, a
  // message: anyone may write to anyone
  const collaboratesIn = followState?.collaborates_in || [];          // a consortium or lab together
  const following = followState?.state === 'connected' || collaboratesIn.length > 0 || Boolean(me?.is_staff);
  const picked = (user.skills || []).filter((s) => s.featured);
  const shownSkills = picked.length ? picked.slice(0, 5) : (user.skills || []).slice(0, 5);
  const otherSkills = (user.skills || []).filter((s) => !shownSkills.includes(s));


  return (
    <StyledCard 
              selected={false} 
              sx={{ cursor: 'pointer' }} 
              onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/profile/${user.id}`);
              }}>
      {/* Checkbox for multi-selection - always visible */}
      {/* <Checkbox
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelect(user.id);
        }}
        onClick={(e) => {
          e.stopPropagation(); // Also stop propagation on click
        }}
        icon={<CheckBoxOutlineBlankIcon />}
        checkedIcon={<CheckBoxIcon />}
        sx={{ 
          position: 'absolute', 
          top: 4, 
          color: 'primary.main',
          right: 4, 
          zIndex: 2,
          padding: 0.5,
          '& .MuiSvgIcon-root': { fontSize: 20 }
        }} 
      {/* /> */}

      <CardContent>
        {/* Who: avatar, name, location and main affiliation */}
        <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={user.avatar}
              alt={`${user.first_name || ''} ${user.last_name || user.username || ''}`}
              // smaller on phones, where two cards share a row
              sx={{ width: { xs: 44, sm: 60 }, height: { xs: 44, sm: 60 }, fontSize: '1.5rem', backgroundColor: theme.palette.primary.avatar, color: 'white' }}
            />
            {user.is_verified && (
              <Tooltip title="Verified member" arrow>
                <VerifiedIcon sx={{ position: 'absolute', bottom: -3, right: -4, fontSize: 19, color: '#1976d2',
                  bgcolor: '#fff', borderRadius: '50%' }} />
              </Tooltip>
            )}
          </Box>
          <Box minWidth={0}>
            {/* a long name wraps to a second line rather than being cut */}
            <Typography title={`${user.first_name || ''} ${user.last_name || ''}`}
              sx={{ fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.2, wordBreak: 'break-word', display: '-webkit-box',
                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', '&:hover': { textDecoration: 'underline' } }}>
              {user.first_name} {user.last_name}
            </Typography>
            {user.location && (
              <Typography variant="body2" fontSize={'.9rem'} color="text.secondary" noWrap>📍{user.location}</Typography>
            )}
          </Box>
        </Box>

        {user.affiliations?.length > 0 && (
          <Typography variant="body2" color="text.secondary" noWrap title={user.affiliations.map((o) => o.name).join(', ')}
            sx={{ mt: 1, mb: user.skills?.length ? 0 : 1 }}>
            🏢 {user.affiliations[0].name}
            {user.affiliations.length > 1 && ` +${user.affiliations.length - 1}`}
          </Typography>
        )}

        {/* Skills: the ones the person picked for this card (up to 5), else the first 5; two lines at most */}
        {user.skills?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1, maxHeight: 44, overflow: 'hidden' }}>
            {shownSkills.map((userSkill) => (
              <Chip
                key={userSkill.id}
                label={userSkill?.skill?.name}
                title={userSkill?.skill?.name}
                size="small"
                sx={{ maxWidth: '100%', bgcolor: theme.palette.primary.xulight, color: theme.palette.secondary.contrastText,
                  height: 20, '& .MuiChip-label': { fontSize: '0.85rem !important', px: 0.75, lineHeight: '20px' } }}
              />
            ))}
            {otherSkills.length > 0 && (
              <Chip label={`+${otherSkills.length}`} size="small" variant="outlined"
                title={otherSkills.map((s) => s?.skill?.name).join(', ')}
                sx={{ borderColor: theme.palette.primary.main, color: theme.palette.primary.main, height: 20, '& .MuiChip-label': { fontSize: '0.85rem !important', px: 0.75, lineHeight: '20px' } }} />
            )}
          </Box>
        )}

        {/* Follow and the actions */}
        <Box
          sx={{ mt: 'auto', pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', gap: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <FollowButton contentType="user" objectId={user.id} size="small" variant="outlined" compact
            compactAll={following} state={followState} />
          {<ActionButtons
            only={following ? undefined : ['message']}
            sx={{ gap: { xs: 0.5, sm: 1 }, mr: { xs: 0, sm: 1 },
              // easy to see and tap; on phones (two cards a row) a little closer together
              '& .MuiIconButton-root': { p: { xs: 0.25 } }, '& .MuiIconButton-root svg': { fontSize: '1.75rem' } }}
            user={user}
            isOwnProfile={false}
            setSuggestDialogOpen={setSuggestDialogOpen}
            setInviteToConsortiumDialogOpen={setInviteToConsortiumDialogOpen}
            setMeetingDialogOpen={setMeetingDialogOpen}
            size="xs"
            fontSize="1.8rem"
            invertColors={true}
          />}
        </Box>
      </CardContent>


      {/* Meeting Schedule Dialog */}
      <MeetingScheduleDialog
        open={meetingDialogOpen}
        onClose={(e) => {
          e.stopPropagation();
          setMeetingDialogOpen(false)}}
        restrictToUsers={[user]}
      />

      {/* Invite to Consortium Dialog */}
      <InviteToConsortiumDialog
        open={inviteToConsortiumDialogOpen}
        onClose={(e) => {
          e.stopPropagation(); 
          setInviteToConsortiumDialogOpen(false)}}
        user={user}
      />

      {/* Suggest Entity Dialog */}
      <SuggestEntityDialog
        open={suggestDialogOpen}
        onClose={(e) => {
          e.stopPropagation();
          setSuggestDialogOpen(false);}}
        user={user}
      />
      
    </StyledCard>

  );
};

export default UserCard;
